import { useEffect, useMemo, useState } from 'react';
import { Container, Sprite, useTick } from '@pixi/react';
import { Assets, Sprite as PixiSprite, Texture } from 'pixi.js';
import { useGameStore } from '../../../store/gameSlice';
import { useHubStore } from '../../../store/hubSlice';
import { hubService } from '../../../services/HubService';
import { audioService } from '../../../services/AudioService';
import { HUB_MAPS } from '../../../data/hub-map.config';
import { HubMapConfig } from '../../../types/hub.types';
import { useHubInput } from '../../../hooks/useHubInput';
import { HubNpcSprite } from './HubNpcSprite';
import { HubPlayerSprite } from './HubPlayerSprite';
import { RemoteHubAvatars } from './RemoteHubAvatars';
import { useHubPresence } from '../../../hooks/useHubPresence';
import { logger } from '../../../services/LoggerService';
import { cinematicService } from '../../../services/CinematicService';
import { runHubArrival } from '../../../services/HubArrivalService';
import {
  registerCinematicScene,
  clearCinematicScene,
  CinematicSceneAdapter,
  CinematicActor,
} from '../../../cinematic/sceneAdapter';

/**
 * Coordinate conventions (aligned with the dungeon):
 * - Map data (mask, NPC placements) is in logical PNG pixels (Y grows downward).
 * - At render, EntityDisplay flips to Y-up via `y → -y` (it assumes Y-up and
 *   multiplies by zoomFactor internally).
 * - The background stays rendered in PNG-coords via a scaled sub-container, with
 *   no Y flip (so the image displays the right way up).
 *
 * Forces a re-render via `useTick` to follow the player position, which lives
 * as a mutable field on `hubService`.
 */
export const HubScene = () => {
  const screenWidth = useGameStore((s) => s.screenWidth);
  const screenHeight = useGameStore((s) => s.screenHeight);
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const mapLoaded = useHubStore((s) => s.mapLoaded);
  const setMapLoaded = useHubStore((s) => s.setMapLoaded);
  const currentHubMapId = useHubStore((s) => s.currentHubMapId);
  const map = HUB_MAPS[currentHubMapId];
  const [, setTick] = useState(0);

  useHubInput();
  const { getAvatars } = useHubPresence();

  useEffect(() => {
    audioService.playMusic('lively-city');
  }, []);

  useEffect(() => {
    let cancelled = false;
    hubService
      .loadMap()
      .then(() => {
        if (!cancelled) {
          setMapLoaded(true);
        }
      })
      .catch((err) => logger.error(err));
    return () => {
      cancelled = true;
      setMapLoaded(false);
    };
  }, [setMapLoaded]);

  useTick((delta) => {
    cinematicService.tick(delta);
    setTick((t) => (t + 1) & 0xffff);
  });

  useEffect(() => {
    const playerActor: CinematicActor = {
      getPosition: () => ({ x: hubService.playerX, y: hubService.playerY }),
      setPosition: (p) => {
        hubService.playerX = p.x;
        hubService.playerY = p.y;
      },
      setFacing: (d) => {
        hubService.facing = d;
      },
      playWalk: () => {
        hubService.cinematicWalking = true;
      },
      playIdle: () => {
        hubService.cinematicWalking = false;
      },
    };

    const adapter: CinematicSceneAdapter = {
      camera: {
        getPosition: () =>
          hubService.cameraOverride ?? {
            x: hubService.playerX,
            y: hubService.playerY,
          },
        setPosition: (p) => {
          hubService.cameraOverride = { x: p.x, y: p.y };
        },
        getZoom: () => hubService.cinematicZoom,
        setZoom: (z) => {
          hubService.cinematicZoom = z;
        },
        setFollow: (actor) => {
          // Stopping the follow holds the current camera position (the override
          // from a prior camera-move), not a snap back to the player — so
          // chained camera-moves pan smoothly instead of teleporting.
          hubService.cameraOverride =
            actor === null
              ? (hubService.cameraOverride ?? {
                  x: hubService.playerX,
                  y: hubService.playerY,
                })
              : null;
        },
      },
      getActor: (ref) => (ref === 'player' ? playerActor : null),
    };

    registerCinematicScene(adapter);
    return () => clearCinematicScene(adapter);
  }, []);

  useEffect(() => {
    if (!mapLoaded) {
      return;
    }
    const spawnId = hubService.consumePendingArrival();
    if (spawnId) {
      void runHubArrival();
    }
  }, [mapLoaded]);

  const awakeTexture = useMemo(
    () => hubTexture(map.awakeBackgroundUrl),
    [map.awakeBackgroundUrl],
  );
  const dreamTexture = useMemo(
    () => (map.dreamBackgroundUrl ? hubTexture(map.dreamBackgroundUrl) : null),
    [map.dreamBackgroundUrl],
  );
  const bubbleTexture = useMemo(
    () => (map.dreamBubble ? hubTexture(map.dreamBubble.maskUrl) : null),
    [map.dreamBubble],
  );

  const [maskNode, setMaskNode] = useState<PixiSprite | null>(null);

  if (!mapLoaded) {
    return null;
  }

  const viewWidth = screenWidth / zoomFactor;
  const viewHeight = screenHeight / zoomFactor;
  const halfW = viewWidth / 2;
  const halfH = viewHeight / 2;
  const { width: mapW, height: mapH } = map;

  const baseCameraX =
    mapW <= viewWidth
      ? mapW / 2
      : clamp(hubService.playerX, halfW, mapW - halfW);
  const baseCameraY =
    mapH <= viewHeight
      ? mapH / 2
      : clamp(hubService.playerY, halfH, mapH - halfH);
  const cameraX = hubService.cameraOverride?.x ?? baseCameraX;
  const cameraY = hubService.cameraOverride?.y ?? baseCameraY;

  const containerX = screenWidth / 2 - cameraX * zoomFactor;
  const containerY = screenHeight / 2 - cameraY * zoomFactor;
  const cinematicZoom = hubService.cinematicZoom;

  // Y-sort on the PNG coord (larger y = lower on screen = in front).
  type SortedSprite =
    | { kind: 'npc'; y: number; npc: HubMap['npcs'][number] }
    | { kind: 'player'; y: number };
  const sortedSprites: SortedSprite[] = [
    ...map.npcs
      .filter((npc) => npc.spriteName)
      .map<SortedSprite>((npc) => ({
        kind: 'npc',
        y: npc.y,
        npc,
      })),
    { kind: 'player' as const, y: hubService.playerY },
  ].sort((a, b) => a.y - b.y);

  return (
    <Container
      scale={cinematicZoom}
      pivot={{ x: screenWidth / 2, y: screenHeight / 2 }}
      x={screenWidth / 2}
      y={screenHeight / 2}
    >
      <Container x={containerX} y={containerY}>
        <Container scale={zoomFactor}>
          <Sprite texture={awakeTexture} x={0} y={0} />

          {map.dreamBubble && dreamTexture && bubbleTexture && (
            <>
              <Sprite
                texture={bubbleTexture}
                ref={(node) => setMaskNode(node as unknown as PixiSprite)}
                x={map.dreamBubble.centerX}
                y={map.dreamBubble.centerY}
                anchor={0.5}
                scale={hubService.bubbleProgress}
              />
              {maskNode && (
                <Sprite texture={dreamTexture} x={0} y={0} mask={maskNode} />
              )}
            </>
          )}
        </Container>

        {/* No transform here: EntityDisplay applies zoomFactor internally and flips Y. */}
        {sortedSprites.map((s) => {
          if (s.kind === 'npc') {
            return <HubNpcSprite key={`npc-${s.npc.npcId}`} npc={s.npc} />;
          }
          return <HubPlayerSprite key="player" />;
        })}
        <RemoteHubAvatars avatars={getAvatars()} />
      </Container>
    </Container>
  );
};

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Hub backgrounds are preloaded by the `hub` asset bundle via `Assets.load`.
// Read them from the Assets cache rather than `Texture.from`, whose legacy
// cache is empty on a cold start (e.g. after an F5 reconnect) — there it would
// kick off a fresh async decode and the background flashes blank meanwhile.
function hubTexture(url: string): Texture {
  return (Assets.get(url) as Texture | undefined) ?? Texture.from(url);
}

type HubMap = HubMapConfig;
